import { createSlice } from '@reduxjs/toolkit';
import { isEmpty } from 'lib';
import { ecommerceInitialState } from './ecommerce.initialState';
import {
  fetchBrandDetails,
  fetchBrandHealth,
  fetchProductTrend,
  fetchUserSatisfaction,
  fetchLatestReviews,
  fetchRiskMonitor,
  fetchReviewShare,
  fetchProductPerformance,
  fetchReviewShareByCategory,
  fetchDigikalaComments,
  fetchDigikalaCommentsCount,
  fetchDigikalaBrands,
  fetchDigikalaCategories,
  fetchDigikalaProducts,
  fetchDigikalaSellers,
  fetchEcommerceBrandCodes,
} from './ecommerce.actions';

const pagedRequestQuery = (query) => {
  const trimmed = String(query ?? '').trim();

  return trimmed.length >= 2 ? trimmed : '';
};

const beginPagedFilter = (list, action) => {
  const page = action.meta.arg?.page ?? 1;
  const query = pagedRequestQuery(action.meta.arg?.q);

  if (page > 1 && (list.query ?? '') !== query) {
    return;
  }

  list.loading = true;
  list.error = null;

  if (page <= 1) {
    list.query = query;
    list.hasMore = false;
  }
};

const rejectPagedFilter = (list, action) => {
  if ((list.query ?? '') !== pagedRequestQuery(action.meta.arg?.q)) {
    return;
  }

  list.loading = false;
  list.error = action.payload;
  list.hasMore = false;
};

const storePagedFilter = (list, action) => {
  const page = action.meta.arg?.page ?? 1;
  const query = pagedRequestQuery(action.meta.arg?.q);

  if ((list.query ?? '') !== query) {
    return;
  }

  const items = action.payload?.items || [];
  const total = action.payload?.total;

  if (page <= 1) {
    list.data = items;
  } else {
    const seenIds = new Set(list.data.map((item) => item.id));
    const nextItems = items.filter((item) => item?.id && !seenIds.has(item.id));

    if (isEmpty(nextItems)) {
      list.page = page;
      list.loading = false;
      list.hasMore = false;
      return;
    }

    list.data.push(...nextItems);
  }

  list.page = page;
  list.loading = false;
  list.error = null;

  if (typeof total === 'number') {
    list.total = total;
    list.hasMore = list.data.length < total;
    return;
  }

  list.hasMore = !isEmpty(items);
};

export const ecommerceSlice = createSlice({
  name: 'ecommerceSlice',
  initialState: ecommerceInitialState,
  reducers: {
    setEcommerceParamValue: (state, action) => {
      const { key, value } = action.payload;
      state[key] = value;
    },
    setReviewsTableParamValue: (state, action) => {
      const { key, value } = action.payload;
      state.reviewsTable[key] = value;
    },
    setReviewsTableAppliedFilters: (state, action) => {
      state.reviewsTable.appliedFilters = action.payload || {};
      state.reviewsTable.page = 1;
    },
    setReviewsTableFilterValue: (state, action) => {
      const { filterKey, value } = action.payload;
      state.reviewsTable.appliedFilters[filterKey] = value;

      if (filterKey === 'brand') {
        state.reviewsTable.appliedFilters.category = [];
        state.reviewsTable.appliedFilters.product = [];
        state.reviewsTable.appliedFilters.seller = [];
      }

      state.reviewsTable.page = 1;
    },
    resetReviewsTableState: (state) => {
      state.reviewsTable = { ...ecommerceInitialState.reviewsTable };
      state.assignedBrands = {
        ...ecommerceInitialState.assignedBrands,
      };
      state.reviewFilterOptions = {
        brands: { ...ecommerceInitialState.reviewFilterOptions.brands },
        categories: {
          ...ecommerceInitialState.reviewFilterOptions.categories,
        },
        products: { ...ecommerceInitialState.reviewFilterOptions.products },
        sellers: { ...ecommerceInitialState.reviewFilterOptions.sellers },
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBrandDetails.pending, (state) => {
        state.brandDetails.loading = true;
        state.brandDetails.error = null;
        state.brandDetails.data = null;
      })
      .addCase(fetchBrandDetails.fulfilled, (state, action) => {
        state.brandDetails.data = action.payload;
        state.brandDetails.loading = false;
        state.brandDetails.error = null;
      })
      .addCase(fetchBrandDetails.rejected, (state, action) => {
        state.brandDetails.loading = false;
        state.brandDetails.error = action.payload;
      })
      .addCase(fetchBrandHealth.pending, (state) => {
        state.brandHealth.loading = true;
        state.brandHealth.error = null;
      })
      .addCase(fetchBrandHealth.fulfilled, (state, action) => {
        state.brandHealth.data = action.payload;
        state.brandHealth.loading = false;
        state.brandHealth.error = null;
      })
      .addCase(fetchBrandHealth.rejected, (state, action) => {
        state.brandHealth.loading = false;
        state.brandHealth.error = action.payload;
      })
      .addCase(fetchProductTrend.pending, (state) => {
        state.productTrend.loading = true;
        state.productTrend.error = null;
      })
      .addCase(fetchProductTrend.fulfilled, (state, action) => {
        state.productTrend.data = action.payload;
        state.productTrend.loading = false;
        state.productTrend.error = null;
      })
      .addCase(fetchProductTrend.rejected, (state, action) => {
        state.productTrend.loading = false;
        state.productTrend.error = action.payload;
      })
      .addCase(fetchUserSatisfaction.pending, (state) => {
        state.userSatisfaction.loading = true;
        state.userSatisfaction.error = null;
      })
      .addCase(fetchUserSatisfaction.fulfilled, (state, action) => {
        state.userSatisfaction.data = action.payload;
        state.userSatisfaction.loading = false;
        state.userSatisfaction.error = null;
      })
      .addCase(fetchUserSatisfaction.rejected, (state, action) => {
        state.userSatisfaction.loading = false;
        state.userSatisfaction.error = action.payload;
      })
      .addCase(fetchRiskMonitor.pending, (state) => {
        state.riskMonitor.loading = true;
        state.riskMonitor.error = null;
      })
      .addCase(fetchRiskMonitor.fulfilled, (state, action) => {
        state.riskMonitor.data = action.payload;
        state.riskMonitor.loading = false;
        state.riskMonitor.error = null;
      })
      .addCase(fetchRiskMonitor.rejected, (state, action) => {
        state.riskMonitor.loading = false;
        state.riskMonitor.error = action.payload;
      })
      .addCase(fetchReviewShare.pending, (state) => {
        state.reviewShare.loading = true;
        state.reviewShare.error = null;
      })
      .addCase(fetchReviewShare.fulfilled, (state, action) => {
        state.reviewShare.data = action.payload;
        state.reviewShare.loading = false;
        state.reviewShare.error = null;
      })
      .addCase(fetchReviewShare.rejected, (state, action) => {
        state.reviewShare.loading = false;
        state.reviewShare.error = action.payload;
      })
      .addCase(fetchProductPerformance.pending, (state) => {
        state.productPerformance.loading = true;
        state.productPerformance.error = null;
      })
      .addCase(fetchProductPerformance.fulfilled, (state, action) => {
        state.productPerformance.data = action.payload;
        state.productPerformance.loading = false;
        state.productPerformance.error = null;
      })
      .addCase(fetchProductPerformance.rejected, (state, action) => {
        state.productPerformance.loading = false;
        state.productPerformance.error = action.payload;
      })
      .addCase(fetchLatestReviews.pending, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.latestReviews.loading = true;
          state.latestReviews.error = null;
        }
      })
      .addCase(fetchLatestReviews.fulfilled, (state, action) => {
        const newData = action.payload || [];
        const existingData = state.latestReviews.data || [];

        if (existingData.length === 0) {
          state.latestReviews.data = newData;
        } else {
          const existingReviewIds = new Set(
            existingData.map((item) => item.review?.id).filter(Boolean)
          );

          const uniqueNewData = newData.filter(
            (item) => !existingReviewIds.has(item.review?.id)
          );

          if (uniqueNewData.length > 0) {
            state.latestReviews.data = [...existingData, ...uniqueNewData];
          }
        }

        if (!action.meta.arg?.silent) {
          state.latestReviews.loading = false;
          state.latestReviews.error = null;
        }
      })
      .addCase(fetchLatestReviews.rejected, (state, action) => {
        if (!action.meta.arg?.silent) {
          state.latestReviews.loading = false;
          state.latestReviews.error = action.payload;
        }
      })
      .addCase(fetchReviewShareByCategory.pending, (state) => {
        state.reviewShareByCategory.loading = true;
        state.reviewShareByCategory.error = null;
      })
      .addCase(fetchReviewShareByCategory.fulfilled, (state, action) => {
        state.reviewShareByCategory.data = action.payload;
        state.reviewShareByCategory.loading = false;
        state.reviewShareByCategory.error = null;
      })
      .addCase(fetchReviewShareByCategory.rejected, (state, action) => {
        state.reviewShareByCategory.loading = false;
        state.reviewShareByCategory.error = action.payload;
      })
      .addCase(fetchEcommerceBrandCodes.pending, (state) => {
        state.assignedBrands.loading = true;
        state.assignedBrands.error = null;
      })
      .addCase(fetchEcommerceBrandCodes.fulfilled, (state, action) => {
        const brands = action.payload || [];
        state.assignedBrands.data = brands;
        state.assignedBrands.loading = false;
        state.assignedBrands.error = null;

        const brandCodes = brands.map((brand) => brand.id).filter(Boolean);
        state.reviewsTable.seedBrandCodes = brandCodes;

        if (!state.reviewsTable.seedBrandCode && !isEmpty(brandCodes)) {
          const [firstBrandCode] = brandCodes;
          state.reviewsTable.seedBrandCode = firstBrandCode;
        }
      })
      .addCase(fetchEcommerceBrandCodes.rejected, (state, action) => {
        state.assignedBrands.loading = false;
        state.assignedBrands.error = action.payload;
      })
      .addCase(fetchDigikalaComments.pending, (state) => {
        state.reviewsTable.loading = true;
        state.reviewsTable.error = null;
      })
      .addCase(fetchDigikalaComments.fulfilled, (state, action) => {
        state.reviewsTable.data = action.payload;
        state.reviewsTable.loading = false;
        state.reviewsTable.hasFetched = true;
        state.reviewsTable.error = null;
      })
      .addCase(fetchDigikalaComments.rejected, (state, action) => {
        state.reviewsTable.loading = false;
        state.reviewsTable.hasFetched = true;
        state.reviewsTable.error = action.payload;
      })
      .addCase(fetchDigikalaCommentsCount.pending, (state) => {
        state.reviewsTable.countLoading = true;
      })
      .addCase(fetchDigikalaCommentsCount.fulfilled, (state, action) => {
        state.reviewsTable.countLoading = false;
        state.reviewsTable.total =
          typeof action.payload === 'number' ? action.payload : null;
      })
      .addCase(fetchDigikalaCommentsCount.rejected, (state) => {
        state.reviewsTable.countLoading = false;
      })
      .addCase(fetchDigikalaBrands.pending, (state, action) => {
        beginPagedFilter(state.reviewFilterOptions.brands, action);
      })
      .addCase(fetchDigikalaBrands.fulfilled, (state, action) => {
        storePagedFilter(state.reviewFilterOptions.brands, action);
      })
      .addCase(fetchDigikalaBrands.rejected, (state, action) => {
        rejectPagedFilter(state.reviewFilterOptions.brands, action);
      })
      .addCase(fetchDigikalaCategories.pending, (state, action) => {
        beginPagedFilter(state.reviewFilterOptions.categories, action);
      })
      .addCase(fetchDigikalaCategories.fulfilled, (state, action) => {
        storePagedFilter(state.reviewFilterOptions.categories, action);
      })
      .addCase(fetchDigikalaCategories.rejected, (state, action) => {
        rejectPagedFilter(state.reviewFilterOptions.categories, action);
      })
      .addCase(fetchDigikalaProducts.pending, (state, action) => {
        beginPagedFilter(state.reviewFilterOptions.products, action);
      })
      .addCase(fetchDigikalaProducts.fulfilled, (state, action) => {
        storePagedFilter(state.reviewFilterOptions.products, action);
      })
      .addCase(fetchDigikalaProducts.rejected, (state, action) => {
        rejectPagedFilter(state.reviewFilterOptions.products, action);
      })
      .addCase(fetchDigikalaSellers.pending, (state, action) => {
        beginPagedFilter(state.reviewFilterOptions.sellers, action);
      })
      .addCase(fetchDigikalaSellers.fulfilled, (state, action) => {
        storePagedFilter(state.reviewFilterOptions.sellers, action);
      })
      .addCase(fetchDigikalaSellers.rejected, (state, action) => {
        rejectPagedFilter(state.reviewFilterOptions.sellers, action);
      });
  },
});

export const {
  setEcommerceParamValue,
  setReviewsTableParamValue,
  setReviewsTableAppliedFilters,
  setReviewsTableFilterValue,
  resetReviewsTableState,
} = ecommerceSlice.actions;
